import { ELocale } from "@rentiohq/shared-frontend/dist/types/i18n.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { Button } from "@rentiohq/web-shared/dist/components";
import { useEffect, useState } from "react";
import rentioLogo from "../../../../assets/svg/full-logo.svg";
import * as S from "./RentioAiWebinarBanner.styles";

// The webinar is given in Dutch, so the banner is shown in Dutch whatever the
// dashboard's language: a broker who uses it in English or French sees at once
// which language the session is in. The texts are still passed to
// getLocalizedText one by one, so the key check keeps seeing them.
const BANNER_LOCALE = ELocale.NL;

interface IProps {
  registrationUrl: string;
}

interface IExample {
  question: string;
  // The answer the assistant gives, one line at a time: a lead line and up to
  // three bullets, which is what the window has room for.
  answer: string[];
  // What Rentio reports under its card once it has the answer.
  found: string;
}

// The questions the animation asks, one per round, in this order.
const EXAMPLES: IExample[] = [
  {
    question: "ai_webinar_banner.examples.vacating.question",
    answer: [
      "ai_webinar_banner.examples.vacating.answer",
      "ai_webinar_banner.examples.vacating.answer_1",
      "ai_webinar_banner.examples.vacating.answer_2",
      "ai_webinar_banner.examples.vacating.answer_3",
    ],
    found: "ai_webinar_banner.examples.vacating.found",
  },
  {
    question: "ai_webinar_banner.examples.average_rent.question",
    answer: [
      "ai_webinar_banner.examples.average_rent.answer",
      "ai_webinar_banner.examples.average_rent.answer_1",
      "ai_webinar_banner.examples.average_rent.answer_2",
      "ai_webinar_banner.examples.average_rent.answer_3",
    ],
    found: "ai_webinar_banner.examples.average_rent.found",
  },
  {
    question: "ai_webinar_banner.examples.vacancy.question",
    answer: [
      "ai_webinar_banner.examples.vacancy.answer",
      "ai_webinar_banner.examples.vacancy.answer_1",
      "ai_webinar_banner.examples.vacancy.answer_2",
      "ai_webinar_banner.examples.vacancy.answer_3",
    ],
    found: "ai_webinar_banner.examples.vacancy.found",
  },
  {
    question: "ai_webinar_banner.examples.arrears.question",
    answer: [
      "ai_webinar_banner.examples.arrears.answer",
      "ai_webinar_banner.examples.arrears.answer_1",
      "ai_webinar_banner.examples.arrears.answer_2",
    ],
    found: "ai_webinar_banner.examples.arrears.found",
  },
];

// The animation from the Rentio Connect email: the question waits while a dot
// travels over the connection to Rentio, Rentio finds the answer, the dot
// travels back, the answer appears line by line, it holds, and it starts again
// with the next question.
type TPhase = "asking" | "toRentio" | "hit" | "found" | "back" | "answered";

// Timings of the animation, in milliseconds.
const ASKING_DELAY = 900;
const TO_RENTIO_DURATION = 1_300;
const HIT_DELAY = 450;
const FOUND_DELAY = 500;
const BACK_DURATION = 1_000;
const LINE_DELAY = 380;
const HOLD_DELAY = 5_200;

const prefersReducedMotion = () =>
  !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// Runs the animation and returns where it is. With reduced motion it stands
// still on the first question, answered.
const useConnectionAnimation = () => {
  const [state, setState] = useState<{
    example: number;
    phase: TPhase;
    lines: number;
  }>({ example: 0, phase: "asking", lines: 0 });

  useEffect(() => {
    if (prefersReducedMotion()) {
      setState({
        example: 0,
        phase: "answered",
        lines: EXAMPLES[0].answer.length,
      });
      return;
    }

    let isStopped = false;
    let timer: number | undefined;

    const schedule = (step: () => void, delay: number) => {
      timer = window.setTimeout(() => {
        if (!isStopped) {
          step();
        }
      }, delay);
    };

    const showLine = (example: number, lines: number) => {
      setState({ example, phase: "answered", lines });

      if (lines < EXAMPLES[example].answer.length) {
        schedule(() => showLine(example, lines + 1), LINE_DELAY);
        return;
      }

      schedule(() => start((example + 1) % EXAMPLES.length), HOLD_DELAY);
    };

    const start = (example: number) => {
      setState({ example, phase: "asking", lines: 0 });
      schedule(() => {
        setState({ example, phase: "toRentio", lines: 0 });
        schedule(() => {
          setState({ example, phase: "hit", lines: 0 });
          schedule(() => {
            setState({ example, phase: "found", lines: 0 });
            schedule(() => {
              setState({ example, phase: "back", lines: 0 });
              schedule(() => showLine(example, 1), BACK_DURATION);
            }, FOUND_DELAY);
          }, HIT_DELAY);
        }, TO_RENTIO_DURATION);
      }, ASKING_DELAY);
    };

    start(0);

    return () => {
      isStopped = true;
      window.clearTimeout(timer);
    };
  }, []);

  return state;
};

const CalendarIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M16 3v4M8 3v4M3 10h18" />
  </svg>
);

const VideoIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="2" y="6" width="14" height="12" rx="2" />
    <path d="M16 10l6-3v10l-6-3z" />
  </svg>
);

const LinkIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7" />
    <path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7" />
  </svg>
);

const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12l5 5 9-10" />
  </svg>
);

export const RentioAiWebinarBanner = (props: IProps) => {
  const { registrationUrl } = props;

  const kicker = getLocalizedText(
    "ai_webinar_banner.kicker",
    {},
    0,
    BANNER_LOCALE,
  );
  const title = getLocalizedText(
    "ai_webinar_banner.title",
    {},
    0,
    BANNER_LOCALE,
  );

  const { example, phase, lines } = useConnectionAnimation();
  const { question, answer, found } = EXAMPLES[example];
  const isFound = phase === "found" || phase === "back" || phase === "answered";

  return (
    <S.Banner aria-label={`${kicker}: ${title}`}>
      <S.Content>
        <S.Kicker>
          <VideoIcon />
          {kicker}
        </S.Kicker>
        <S.Title>{title}</S.Title>
        <S.When>
          <CalendarIcon />
          {getLocalizedText("ai_webinar_banner.when", {}, 0, BANNER_LOCALE)}
        </S.When>
        <S.Description>
          {getLocalizedText(
            "ai_webinar_banner.description",
            {},
            0,
            BANNER_LOCALE,
          )}
        </S.Description>
        <S.Action>
          <Button
            appearance="primary"
            onClick={() =>
              window.open(registrationUrl, "_blank", "noopener,noreferrer")
            }
          >
            {getLocalizedText("ai_webinar_banner.action", {}, 0, BANNER_LOCALE)}
          </Button>
        </S.Action>
      </S.Content>

      {/* A question in an AI assistant travels over the connection to Rentio,
          and the answer comes back. */}
      <S.Visual aria-hidden="true">
        <S.Flow>
          <S.Window>
            <S.WindowBar>
              <S.WindowDot $color={S.COLORS.windowRed} />
              <S.WindowDot $color={S.COLORS.windowYellow} />
              <S.WindowDot $color={S.COLORS.windowGreen} />
              <S.WindowTitle>
                {getLocalizedText(
                  "ai_webinar_banner.assistant",
                  {},
                  0,
                  BANNER_LOCALE,
                )}
              </S.WindowTitle>
            </S.WindowBar>
            <S.WindowBody>
              <S.Question>
                {getLocalizedText(question, {}, 0, BANNER_LOCALE)}
              </S.Question>
              {phase !== "answered" && (
                <S.Status>
                  <S.Spinner />
                  {getLocalizedText(
                    "ai_webinar_banner.asking",
                    {},
                    0,
                    BANNER_LOCALE,
                  )}
                </S.Status>
              )}
              <S.Answer>
                {answer.map((key, index) => (
                  <S.AnswerLine key={key} $isVisible={index < lines}>
                    {index > 0 && <S.Bullet />}
                    {getLocalizedText(key, {}, 0, BANNER_LOCALE)}
                  </S.AnswerLine>
                ))}
              </S.Answer>
            </S.WindowBody>
          </S.Window>

          <S.Wire>
            {phase === "toRentio" && (
              <S.Packet $isReturning={false} $duration={TO_RENTIO_DURATION} />
            )}
            {phase === "back" && (
              <S.Packet $isReturning={true} $duration={BACK_DURATION} />
            )}
            <S.Link $isPulsing={phase === "toRentio"}>
              <LinkIcon />
            </S.Link>
          </S.Wire>

          <S.RentioCard $isHit={phase === "hit"}>
            <img src={rentioLogo} alt="" />
            <S.Check $isVisible={isFound}>
              <CheckIcon />
            </S.Check>
            <S.Found $isVisible={isFound}>
              <CheckIcon />
              {getLocalizedText(found, {}, 0, BANNER_LOCALE)}
            </S.Found>
          </S.RentioCard>
        </S.Flow>
      </S.Visual>
    </S.Banner>
  );
};
